import { API, graphqlOperation } from "aws-amplify";
import { listCandidateProfiles } from "../graphql/queries";
import { createCandidateProfile, updateCandidateProfile } from "../graphql/mutations";

/**
 * Utility functions for candidate profile management
 */

/**
 * Check if a userID is in UUID format (indicates a real registered user)
 * @param {string} userID - The userID to check
 * @returns {boolean} True if userID is in UUID format
 */
export const isRealUser = (userID) => {
  if (!userID || typeof userID !== 'string') return false;
  
  // UUID v4 pattern: 8-4-4-4-12 hexadecimal characters with hyphens
  // Example: e1238dca-3021-70f0-58f3-b374412e988b
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidPattern.test(userID);
};

/**
 * Check if a userID is a temporary profile (includes old company-temp format)
 * @param {string} userID - The userID to check
 * @returns {boolean} True if userID is temporary
 */
export const isTemporaryProfile = (userID) => {
  if (!userID || typeof userID !== 'string') return false;
  return userID.startsWith('temp-signin-less-') || userID.startsWith('company-temp-');
};

/**
 * Fetch all candidate profiles for a given email using pagination
 * @param {string} email - The email to search for
 * @returns {Promise<Array>} Array of all candidate profiles for the email
 */
export const fetchAllCandidatesForEmail = async (email) => {
  const normalizedEmail = email.toLowerCase().trim();
  let allCandidates = [];
  let nextToken = null;
  
  console.log(`🔍 Fetching all candidates for email: ${normalizedEmail}`);
  
  try {
    do {
      const response = await API.graphql(
        graphqlOperation(listCandidateProfiles, {
          filter: {
            email: { eq: normalizedEmail }
          },
          limit: 100, // Use larger limit for better performance
          nextToken: nextToken
        })
      );
      
      const candidates = response.data.listCandidateProfiles.items;
      allCandidates.push(...candidates);
      nextToken = response.data.listCandidateProfiles.nextToken;
      
      console.log(`📄 Fetched ${candidates.length} candidates in this batch (Total: ${allCandidates.length})`);
      
    } while (nextToken);
    
    console.log(`✅ Total candidates found for ${normalizedEmail}: ${allCandidates.length}`);
    return allCandidates;
    
  } catch (error) {
    console.error('❌ Error fetching candidates:', error);
    throw error;
  }
};

/**
 * Select the best candidate profile based on priority rules
 * @param {Array} candidates - Array of candidate profiles
 * @returns {Object|null} Best candidate profile or null if none found
 */
export const selectBestCandidate = (candidates) => {
  if (!candidates || candidates.length === 0) {
    return null;
  }
  
  console.log(`🎯 Selecting best candidate from ${candidates.length} profiles`);
  
  // Separate candidates by type and log each one
  const realUsers = [];
  const temporaryProfiles = [];
  const unknownProfiles = [];
  
  candidates.forEach((candidate, index) => {
    const isReal = isRealUser(candidate.userID);
    const isTemp = isTemporaryProfile(candidate.userID);
    
    console.log(`📋 Candidate ${index + 1}:`, {
      id: candidate.id,
      userID: candidate.userID,
      fullName: candidate.fullName,
      email: candidate.email,
      isReal,
      isTemp,
      hasResume: !!candidate.resumeURL,
      hasProfile: !!candidate.fullName
    });
    
    if (isReal) {
      realUsers.push(candidate);
    } else if (isTemp) {
      temporaryProfiles.push(candidate);
    } else {
      unknownProfiles.push(candidate);
    }
  });
  
  console.log(`👥 Categorized: ${realUsers.length} real users, ${temporaryProfiles.length} temporary, ${unknownProfiles.length} unknown`);
  
  // Priority 1: Real registered users (UUID userIDs)
  if (realUsers.length > 0) {
    const bestReal = realUsers[0]; // First real user found
    console.log(`✨ Selected real user:`, {
      id: bestReal.id,
      userID: bestReal.userID,
      fullName: bestReal.fullName
    });
    return bestReal;
  }
  
  // Priority 2: Existing temporary profiles (newest first)
  if (temporaryProfiles.length > 0) {
    const sortedTemporary = temporaryProfiles.sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    );
    const bestTemporary = sortedTemporary[0];
    console.log(`🔄 Selected existing temporary profile:`, {
      id: bestTemporary.id,
      userID: bestTemporary.userID,
      fullName: bestTemporary.fullName,
      createdAt: bestTemporary.createdAt
    });
    return bestTemporary;
  }
  
  // Priority 3: Unknown profiles (fallback)
  if (unknownProfiles.length > 0) {
    const bestUnknown = unknownProfiles[0];
    console.log(`⚠️ Selected unknown profile type:`, {
      id: bestUnknown.id,
      userID: bestUnknown.userID,
      fullName: bestUnknown.fullName
    });
    return bestUnknown;
  }
  
  console.log('❓ No suitable candidate found');
  return null;
};

/**
 * Update candidate profile if name is different
 * @param {Object} candidate - The candidate profile
 * @param {string} newName - The new name to update
 * @returns {Promise<Object>} Updated candidate profile
 */
export const updateCandidateNameIfNeeded = async (candidate, newName) => {
  if (!candidate || !newName) return candidate;
  
  // Normalize names for comparison
  const currentName = (candidate.fullName || '').trim();
  const normalizedNewName = newName.trim();
  
  if (currentName !== normalizedNewName && normalizedNewName !== '') {
    console.log(`📝 Updating candidate name from "${currentName}" to "${normalizedNewName}"`);
    
    try {
      const updateInput = {
        id: candidate.id,
        fullName: normalizedNewName
      };
      
      const updateResponse = await API.graphql(
        graphqlOperation(updateCandidateProfile, { input: updateInput })
      );
      
      const updatedCandidate = updateResponse.data.updateCandidateProfile;
      console.log(`✅ Successfully updated candidate name`);
      return updatedCandidate;
      
    } catch (error) {
      console.error('❌ Error updating candidate name:', error);
      // Return original candidate if update fails
      return candidate;
    }
  }
  
  return candidate;
};

/**
 * Create a new temporary candidate profile
 * @param {string} candidateName - The candidate's name
 * @param {string} candidateEmail - The candidate's email
 * @returns {Promise<Object>} Created candidate profile
 */
export const createTemporaryProfile = async (candidateName, candidateEmail) => {
  const normalizedEmail = candidateEmail.toLowerCase().trim();
  
  const candidateProfileInput = {
    userID: `temp-signin-less-${Date.now()}`, // Unified temporary format
    fullName: candidateName.trim(),
    email: normalizedEmail,
    t_c: true, // Required field
    private_Candidate: true, // Mark as temporary/private
    isPublic: false // Not public
  };

  console.log('🆕 Creating new temporary candidate profile:', candidateProfileInput);

  try {
    const candidateResponse = await API.graphql(
      graphqlOperation(createCandidateProfile, { input: candidateProfileInput })
    );

    if (!candidateResponse.data.createCandidateProfile) {
      throw new Error('Failed to create temporary candidate profile');
    }

    const newProfile = candidateResponse.data.createCandidateProfile;
    console.log(`✅ Created new temporary profile: ${newProfile.id}`);
    return newProfile;
    
  } catch (error) {
    console.error('❌ Error creating temporary profile:', error);
    throw error;
  }
};

/**
 * Main function to find or create a candidate profile
 * @param {string} candidateName - The candidate's name
 * @param {string} candidateEmail - The candidate's email
 * @returns {Promise<Object>} Object containing candidateProfile and isExistingCandidate
 */
export const findOrCreateCandidateProfile = async (candidateName, candidateEmail) => {
  console.log('🚀 Starting findOrCreateCandidateProfile');
  console.log(`📧 Candidate: ${candidateName} (${candidateEmail})`);
  
  try {
    // Step 1: Fetch all candidate profiles for the email
    const allCandidates = await fetchAllCandidatesForEmail(candidateEmail);
    
    // Step 2: Select the best candidate based on priority rules
    let selectedCandidate = selectBestCandidate(allCandidates);
    let isExistingCandidate = false;
    
    if (selectedCandidate) {
      // Use existing candidate profile without modifying the name
      isExistingCandidate = true;
      
      console.log(`🎉 Using existing candidate profile: ${selectedCandidate.id}`);
    } else {
      // Step 4: Create new temporary profile if no suitable candidate found
      selectedCandidate = await createTemporaryProfile(candidateName, candidateEmail);
      isExistingCandidate = false;
      
      console.log(`🎉 Created new temporary candidate profile: ${selectedCandidate.id}`);
    }
    
    // Return the result
    return {
      candidateProfile: selectedCandidate,
      candidateProfileID: selectedCandidate.id,
      isExistingCandidate: isExistingCandidate,
      isRealUser: isRealUser(selectedCandidate.userID),
      isTemporary: isTemporaryProfile(selectedCandidate.userID)
    };
    
  } catch (error) {
    console.error('❌ Error in findOrCreateCandidateProfile:', error);
    throw error;
  }
};

/**
 * Create simplified meeting config
 * @param {Object} params - Configuration parameters
 * @returns {string} JSON string of meeting configuration
 */
/**
 * Create in-memory demo candidate profile (no database storage)
 * @param {string} candidateName - Demo candidate name
 * @param {string} candidateEmail - Demo candidate email
 * @returns {Object} In-memory demo candidate profile data
 */
export const createInMemoryDemoProfile = (candidateName, candidateEmail) => {
  console.log('🧠 Creating in-memory demo profile (no database load)');
  
  const tempProfileId = `demo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  // 🧠 Profile exists only in browser memory - not saved to database
  const inMemoryProfile = {
    id: tempProfileId,
    fullName: candidateName.trim(),
    email: candidateEmail.toLowerCase().trim(),
    private_Candidate: true,
    isDemo: true,
    temporaryProfile: true,
    workStatus: 'Demo Candidate',
    description: 'In-memory demo profile - no database storage',
    userID: `demo-user-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    
    // 🧠 Mark as in-memory for identification
    isInMemory: true,
    expiresAt: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(), // 2 hours browser session
    
    // 📝 Minimal required fields for compatibility
    t_c: true,
    gender: 'Not specified',
    mobileNumber: '+1-000-000-0000',
    isPublic: false
  };

  // 💾 Store in localStorage for session persistence
  try {
    localStorage.setItem(`demo-profile-${tempProfileId}`, JSON.stringify(inMemoryProfile));
    localStorage.setItem('candidateProfile', JSON.stringify(inMemoryProfile));
    localStorage.setItem('candidateProfileId', tempProfileId);
    localStorage.setItem('candidateProfileEmailID', candidateEmail.toLowerCase());
    
    console.log('✅ In-memory demo profile created:', {
      id: tempProfileId,
      name: candidateName,
      email: candidateEmail,
      isInMemory: true,
      storedInLocalStorage: true
    });
  } catch (storageError) {
    console.warn('⚠️ localStorage unavailable, profile exists in memory only:', storageError);
  }
  
  return {
    candidateProfile: inMemoryProfile,
    candidateProfileID: tempProfileId,
    isExistingCandidate: false,
    isDemoProfile: true,
    isInMemory: true
  };
};

export const createMeetingConfig = ({
  companyID,
  companyName,
  jobTitle,
  jdID,
  interviewType, // 'company-portal', 'sign-in-less', or 'demo'
  candidateName, // The name provided for this specific interview (can be null for demo)
  numberOfQuestions, // Number of questions for the interview (default: 5)
  isDemo, // Optional flag to explicitly mark demo interviews
  jobData // Optional job details for demo interviews (description, skills, category, experience)
}) => {
  const config = {
    companyID: companyID || 'unknown',
    companyName: companyName || 'Company',
    jobTitle: jobTitle || 'Interview',
    jdID: jdID || null,
    interviewType: interviewType || 'unknown',
    candidateName: candidateName || null, // Store the specific name for this interview (null for demo)
    numberOfQuestions: numberOfQuestions || 5, // Default to 5 questions if not provided
    createdAt: new Date().toISOString()
  };
  
  // Add isDemo flag only if explicitly provided (not required)
  if (isDemo !== undefined) {
    config.isDemo = isDemo;
  }
  
  // 📋 Add job data for demo interviews (helps backend generate relevant questions)
  if (jobData) {
    config.jobData = {
      jobDescription: jobData.jobDescription || null,
      skills: jobData.skills || null,
      category: jobData.category || null,
      experienceLevel: jobData.experienceLevel || null
    };
  }
  
  return JSON.stringify(config);
};
